import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import './Login.css';

const ONBOARDING_KEY = 'seven_onboarding_done_v1';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const st = location.state;
    if (st?.postSetupUsername) {
      setUsername(st.postSetupUsername);
    }
  }, [location.state]);

  useEffect(() => {
    const notice = localStorage.getItem('auth_notice');
    if (!notice) return;
    localStorage.removeItem('auth_notice');
    toast.warning(notice, { position: 'top-center', autoClose: 6000 });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return; // Prevenir múltiples envíos
    
    // Aplicar trim() para eliminar espacios al inicio y final
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();
    
    // Validar que no estén vacíos después del trim
    if (!trimmedUsername || !trimmedPassword) {
      toast.error('Usuario y contraseña son requeridos');
      return;
    }
    
    setLoading(true);

    try {
      const result = await login(trimmedUsername, trimmedPassword, null);

      if (result.success) {
        toast.success('Inicio de sesión exitoso');
        const roles = result.user?.roles || [];
        const isOwner = roles.some((r) => r.name === 'owner');
        const isProviderUser = roles.some((r) => r.name === 'provider');
        let skipOnboarding = false;
        try {
          skipOnboarding = localStorage.getItem(ONBOARDING_KEY) === '1';
        } catch {
          skipOnboarding = false;
        }
        if (isProviderUser && !result.user?.is_superuser) {
          navigate('/provider-ads');
        } else if (isOwner && !skipOnboarding) {
          navigate('/onboarding');
        } else {
          navigate('/');
        }
      } else {
        // Mostrar mensaje específico de error
        const errorMessage = result.error || 'Usuario y/o contraseña incorrectos';
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error('Error en handleSubmit:', error);
      // Determinar el mensaje de error específico
      let errorMessage = 'Error al iniciar sesión';
      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = 'Usuario y/o contraseña incorrectos';
        } else if (error.response.status === 404) {
          errorMessage = 'Servicio no disponible. Intente más tarde.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.message?.includes('Timeout')) {
        errorMessage = 'El servidor no respondió. Verifique su conexión.';
      }
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img 
              src="/logo-seven-banner.png" 
              alt="SEVEN Gestión Deportiva" 
              className="login-logo-img"
            />
          </div>
          <p>Iniciar Sesión</p>
        </div>
        <form onSubmit={handleSubmit} method="POST" className="login-form">
          <div className="form-group">
            <label>Usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onBlur={(e) => setUsername(e.target.value.trim())} // Trim al perder el foco
              required
              placeholder="Ingrese su usuario"
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Ingrese su contraseña"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>
        <div className="login-footer">
          <p>Versión 1.0.0</p>
        </div>
      </div>
    </div>
  );
};

export default Login;

